/*
  Client-side category filtering for the grouped coffee collection. Progressive enhancement:
  with JS disabled every group and card renders (see main-collection-grouped.liquid), so the
  page is never left in a broken state. This only hides/shows what is already in the DOM.

  The controls are a labelled group of toggle buttons (aria-pressed), not a tablist: they all
  filter the one shared grid rather than swapping between separate panels. The result of each
  filter is announced through the [data-showing] role="status" region.
*/
class CollectionTabs extends HTMLElement {
  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll('.collection-tab'));
    this.cards = Array.from(this.querySelectorAll('.grid__item[data-category]'));
    this.groups = Array.from(this.querySelectorAll('[data-group]'));
    this.showing = this.querySelector('[data-showing]');
    this.showingTemplate = this.showing ? this.showing.textContent.trim() : '';

    // Native <button> handles Enter/Space activation and Tab order, so a click listener is all
    // the toggle-button group needs — no roving tabindex or arrow-key handling required.
    this.buttons.forEach((button) => {
      button.addEventListener('click', () => this.activate(button));
    });

    // Sorting reloads through Shopify's native sort_by; wire the select if the merchant enabled it.
    this.sortSelect = this.querySelector('[data-sort-select]');
    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', (event) => this.onSortChange(event));
    }

    // A sort reload drops the client-side filter, so we stash the active category in the URL hash
    // (#c=<handle>) — a hash, not a query param, to keep Shopify's faceted URLs clean for SEO.
    this.restoreFromHash();
  }

  restoreFromHash() {
    const match = window.location.hash.match(/^#c=(.+)$/);
    if (!match) return;
    const handle = decodeURIComponent(match[1]);
    // A bookmarked hash can point at a category that no longer renders a button (dropped to 0
    // products). Falling back to "all" keeps every card visible instead of an empty grid.
    const button =
      this.buttons.find((item) => item.dataset.filter === handle) ||
      this.buttons.find((item) => item.dataset.filter === 'all');
    // activate() never moves focus, so this is safe to call on load.
    if (button) this.activate(button);
  }

  activate(button) {
    const filter = button.dataset.filter;

    this.buttons.forEach((item) => {
      const isActive = item === button;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    this.writeHash(filter);

    let visibleCount = 0;
    this.cards.forEach((card) => {
      const match = filter === 'all' || card.dataset.category === filter;
      card.hidden = !match;
      if (match) visibleCount += 1;
    });

    // A group whose cards are all filtered out should drop its heading too.
    this.groups.forEach((group) => {
      const hasVisible = group.querySelector('.grid__item[data-category]:not([hidden])') !== null;
      group.hidden = !hasVisible;
    });

    this.updateShowing(visibleCount);
  }

  writeHash(filter) {
    const base = window.location.pathname + window.location.search;
    const url = filter === 'all' ? base : `${base}#c=${encodeURIComponent(filter)}`;
    // replaceState (not location.hash) avoids a history entry and any scroll jump.
    window.history.replaceState(null, '', url);
  }

  onSortChange(event) {
    const url = new URL(window.location.href);
    const value = event.target.value;
    if (value) {
      url.searchParams.set('sort_by', value);
    } else {
      url.searchParams.delete('sort_by');
    }
    const active = this.buttons.find((item) => item.classList.contains('is-active'));
    const filter = active ? active.dataset.filter : 'all';
    url.hash = filter === 'all' ? '' : `c=${encodeURIComponent(filter)}`;
    window.location.assign(url.toString());
  }

  updateShowing(count) {
    if (!this.showing || !this.showingTemplate) return;
    // Reuse the Liquid-rendered string as a template so the copy stays localizable.
    // the localized string must contain exactly one number
    this.showing.textContent = this.showingTemplate.replace(/\d+/, count);
  }
}

customElements.define('collection-tabs', CollectionTabs);
